import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Upload, Trash2, Copy, Image as ImageIcon, Film, Music } from 'lucide-react';
import { toast } from 'sonner';

const MediaLibrary = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState('all');
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadMedia();
  }, []);

  const loadMedia = async () => {
    const token = localStorage.getItem('cms_token');
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/cms/media`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response.json();
      setMedia(data);
    } catch (error) {
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const token = localStorage.getItem('cms_token');

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/cms/media/upload`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          }
        );

        if (!response.ok) throw new Error('Upload failed');

        toast.success(`${file.name} uploaded successfully`);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    setUploading(false);
    loadMedia();
  };

  const handleDelete = async (mediaId, filename) => {
    if (!window.confirm(`Delete ${filename}?`)) return;

    const token = localStorage.getItem('cms_token');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/cms/media/${mediaId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error('Delete failed');

      toast.success('Media deleted');
      loadMedia();
    } catch (error) {
      toast.error('Failed to delete media');
    }
  };

  const copyUrl = (url) => {
    const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${url}`;
    navigator.clipboard.writeText(fullUrl);
    toast.success('URL copied to clipboard!');
  };

  const filteredMedia = filter === 'all' ? media : media.filter((m) => m.file_type === filter);

  const getIcon = (type) => {
    switch (type) {
      case 'image':
        return <ImageIcon size={24} />;
      case 'video':
        return <Film size={24} />;
      case 'audio':
        return <Music size={24} />;
      default:
        return <ImageIcon size={24} />;
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-purple-500">Loading media...</div>;
  }

  return (
    <div data-testid="media-library">
      <div className="mb-8">
        <h1 className="text-4xl font-black mb-2">Media Library</h1>
        <p className="text-gray-400">Upload and manage your media files</p>
      </div>

      {/* Upload Area */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/5 border-2 border-dashed border-white/20 rounded-2xl p-12 mb-8 text-center hover:border-purple-500/50 transition-all cursor-pointer"
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload size={48} className="mx-auto mb-4 text-gray-400" />
        <p className="text-lg font-bold mb-2">
          {uploading ? 'Uploading...' : 'Click to upload or drag files here'}
        </p>
        <p className="text-sm text-gray-400">
          Supports: Images (JPG, PNG, GIF, WebP), Videos (MP4, WebM), Audio (MP3, WAV, OGG)
        </p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,video/*,audio/*"
          onChange={handleFileUpload}
          className="hidden"
          data-testid="file-upload-input"
        />
      </motion.div>

      {/* Filter */}
      <div className="flex gap-3 mb-6">
        {['all', 'image', 'video', 'audio'].map((filterType) => (
          <button
            key={filterType}
            onClick={() => setFilter(filterType)}
            data-testid={`filter-${filterType}`}
            className={`px-6 py-2 rounded-full text-sm font-bold transition-all ${
              filter === filterType
                ? 'bg-purple-600 text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            {filterType.charAt(0).toUpperCase() + filterType.slice(1)}
          </button>
        ))}
      </div>

      {/* Media Grid */}
      {filteredMedia.length === 0 ? (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-12 text-center">
          <p className="text-gray-400 mb-2">No media files yet</p>
          <p className="text-sm text-gray-500">Upload your first file using the area above</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {filteredMedia.map((item) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white/5 border border-white/10 rounded-xl overflow-hidden hover:border-purple-500/50 transition-all group"
            >
              {/* Preview */}
              <div className="aspect-square bg-black/30 flex items-center justify-center relative">
                {item.file_type === 'image' ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${item.url}`}
                    alt={item.original_filename}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="text-gray-500">{getIcon(item.file_type)}</div>
                )}
                <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    onClick={() => copyUrl(item.url)}
                    data-testid={`copy-url-${item.id}`}
                    className="p-3 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
                    title="Copy URL"
                  >
                    <Copy size={20} />
                  </button>
                  <button
                    onClick={() => handleDelete(item.id, item.original_filename)}
                    data-testid={`delete-media-${item.id}`}
                    className="p-3 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>

              {/* Info */}
              <div className="p-3">
                <p className="text-sm font-bold truncate" title={item.original_filename}>
                  {item.original_filename}
                </p>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-gray-500">
                    {(item.size / 1024).toFixed(1)} KB
                  </span>
                  <span className="text-xs px-2 py-1 bg-purple-500/20 text-purple-400 rounded">
                    {item.file_type}
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MediaLibrary;